/**
 * 처방 등록 월(yyyyMM) 도우미.
 * 07 처방 등록 현황의 선택지는 서버 /months 가 정한다 — 여기서는 05 업체·목 데이터용 월 계산만 둔다.
 */

const SELECTABLE_MONTHS = 12;

const toYearMonth = (year: number, monthIndex: number) => {
  const date = new Date(year, monthIndex, 1);
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/** 조회 가능한 12개월, 최신순 (첫 값 = 직전 월 = 서버 기본값) */
export const getSelectableRegistrationMonths = (today = new Date()) =>
  Array.from({ length: SELECTABLE_MONTHS }, (_, index) =>
    toYearMonth(today.getFullYear(), today.getMonth() - 1 - index),
  );

/** 현재 월 (yyyyMM) */
export const getCurrentRegistrationMonth = (today = new Date()) =>
  toYearMonth(today.getFullYear(), today.getMonth());

/** '202609' → '2026년 09월' */
const formatYearMonthLabel = (yearMonth: string) =>
  `${yearMonth.slice(0, 4)}년 ${yearMonth.slice(4, 6)}월`;

/**
 * 07 년월 드롭다운 선택지 — (등록 월, 정산 월). 정산 월이 null 이면 등록 월 전체 집계
 * (서버에 settlementYearMonth 를 보내지 않는다).
 */
export interface RegistrationPeriodOption {
  registrationYearMonth: string;
  settlementYearMonth: string | null;
}

/**
 * 등록·정산 월 드롭다운 라벨 — 년도는 앞의 년도 드롭다운에서 고르므로 월만 보인다.
 * '09월 등록 / 11월 정산'. 정산 월이 다음 해면 정산 쪽에만 년도를 붙인다('12월 등록 / 2026년 01월 정산').
 * 정산 월이 없으면 '10월 등록'. 정산 월은 서버(/months)가 준 값 그대로 — 등록 월 + 1개월로 계산하지 않는다.
 */
export const formatRegistrationPeriod = ({
  registrationYearMonth,
  settlementYearMonth,
}: RegistrationPeriodOption) => {
  const registration = `${registrationYearMonth.slice(4, 6)}월 등록`;
  if (!settlementYearMonth) return registration;
  const settlement =
    settlementYearMonth.slice(0, 4) === registrationYearMonth.slice(0, 4)
      ? `${settlementYearMonth.slice(4, 6)}월 정산`
      : `${formatYearMonthLabel(settlementYearMonth)} 정산`;
  return `${registration} / ${settlement}`;
};

/**
 * 등록 년도 드롭다운 선택지(yyyy), 최신순 — 올해부터 첫 등록 년도까지.
 * 연도 목록 API 가 없어 화면에서 만든다. 파트너스테이션 데이터는 2025년부터 있다.
 */
export const FIRST_REGISTRATION_YEAR = 2025;

export const getRegistrationYearOptions = (today = new Date()) => {
  const thisYear = today.getFullYear();
  return Array.from(
    { length: Math.max(1, thisYear - FIRST_REGISTRATION_YEAR + 1) },
    (_, index) => String(thisYear - index),
  );
};

/**
 * 한 해의 /months 응답(최신순)으로 등록·정산 월 드롭다운 선택지를 만든다.
 * - 올해(currentMonth 를 넘긴 경우): 기본값은 항상 현재 월. 현재 월 조합이 아직 없으면
 *   (이번 달 등록 이미지가 없음) 정산 월 없는 현재 월 선택지를 최신순 자리에 끼워 넣는다.
 * - 지난해(currentMonth 없음): 서버가 준 그대로, 기본값은 첫 조합. 비어 있으면 기본값도 없다.
 */
export const buildRegistrationPeriodOptions = (
  periods: ReadonlyArray<{
    registrationYearMonth: string;
    settlementYearMonth: string;
  }>,
  currentMonth?: string,
): {
  options: RegistrationPeriodOption[];
  defaultOption: RegistrationPeriodOption | undefined;
} => {
  const options: RegistrationPeriodOption[] = [...periods];
  if (!currentMonth) return { options, defaultOption: options[0] };
  let defaultOption = options.find(
    (option) => option.registrationYearMonth === currentMonth,
  );
  if (!defaultOption) {
    defaultOption = {
      registrationYearMonth: currentMonth,
      settlementYearMonth: null,
    };
    const insertAt = options.findIndex(
      (option) => option.registrationYearMonth < currentMonth,
    );
    options.splice(insertAt < 0 ? options.length : insertAt, 0, defaultOption);
  }
  return { options, defaultOption };
};

/** '202609' → '2026.09' */
export const formatRegistrationMonth = (yearMonth: string) =>
  `${yearMonth.slice(0, 4)}.${yearMonth.slice(4, 6)}`;
